import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Calendar, 
  DollarSign, 
  ChevronRight, 
  ChevronLeft,
  X,
  Plus,
  CheckCircle2,
  Loader2,
  AlertCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface CreateGiftGroupFlowProps {
  onNavigate: (screen: string, params?: any) => void;
}

export function CreateGiftGroupFlow({ onNavigate }: CreateGiftGroupFlowProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [giftDate, setGiftDate] = useState('');
  const [giftTime, setGiftTime] = useState('');
  const [groupType, setGroupType] = useState('');
  const [giftees, setGiftees] = useState<string[]>([]);
  const [gifters, setGifters] = useState<string[]>([]);
  const [eventTitle, setEventTitle] = useState('');
  const [recommendedAmount, setRecommendedAmount] = useState('25');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string>('');
  const isAuthenticated = api.isAuthenticated();

  // Generate title based on gift group data
  const generateTitle = () => {
    const isExchangeType = ['gift-exchange', 'white-elephant', 'gift-circle'].includes(groupType);
    
    // Extract first name from email (before @ or .)
    const getNameFromContact = (contact: string): string => {
      const namePart = contact.split('@')[0].split('.')[0];
      // Capitalize first letter
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    };

    // Determine occasion from groupType
    const getOccasion = (type: string): string => {
      const occasions: Record<string, string> = {
        'single-giftee': 'Birthday',
        'multiple-giftees': 'Anniversary',
        'individual-gifts': 'Team Celebration',
        'gift-exchange': 'Gift Exchange',
        'white-elephant': 'White Elephant',
        'gift-circle': 'Gift Circle',
      };
      return occasions[type] || 'Gift';
    };

    // Generate title based on type
    if (isExchangeType) {
      const occasion = getOccasion(groupType);
      const month = giftDate ? new Date(giftDate).toLocaleDateString('en-US', { month: 'long' }) : '';
      return month ? `${month} ${occasion}` : occasion;
    } else if (giftees.length === 1) {
      // Single giftee
      const name = getNameFromContact(giftees[0]);
      const occasion = getOccasion(groupType);
      return `${name}'s ${occasion}`;
    } else if (giftees.length === 2) {
      // Two giftees
      const name1 = getNameFromContact(giftees[0]);
      const name2 = getNameFromContact(giftees[1]);
      const occasion = getOccasion(groupType);
      return `${name1} & ${name2}'s ${occasion}`;
    } else if (giftees.length > 2) {
      // Multiple giftees
      const firstName = getNameFromContact(giftees[0]);
      const occasion = getOccasion(groupType);
      return `${firstName} & Family ${occasion}`;
    }
    
    return 'Gift Group';
  };

  // Auto-generate title when user moves to step 4
  useEffect(() => {
    if (step === 4 && !eventTitle) {
      setEventTitle(generateTitle());
    }
  }, [step]);

  // Check for pending gift group data and load it
  useEffect(() => {
    const pendingData = localStorage.getItem('pendingGiftGroup');
    if (pendingData) {
      try {
        const data = JSON.parse(pendingData);
        setEventTitle(data.title || '');
        setGroupType(data.groupType || '');
        setGiftDate(data.giftDate || '');
        setGiftTime(data.giftTime || '');
        setRecommendedAmount(data.recommendedContribution?.toString() || '25');
        setGiftees(data.giftees || []);
        setGifters(data.gifters || []);
        
        // Skip to the final step (review)
        setStep(5);
        
        toast.info('Welcome back!', {
          description: 'Your gift group details have been restored.',
        });
      } catch (err) {
        console.error('Failed to load pending gift group:', err);
        localStorage.removeItem('pendingGiftGroup');
      }
    }
  }, []);

  // Fetch current user info and auto-add as gifter
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = await api.getCurrentUser();
        const userContact = user.email || user.phone || '';
        setCurrentUserEmail(userContact);
        // Auto-add current user as a gifter only if not already in the list
        if (userContact && !gifters.includes(userContact)) {
          setGifters(prev => prev.length > 0 ? prev : [userContact]);
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err);
      }
    };
    
    // Only fetch if authenticated
    if (api.isAuthenticated()) {
      fetchUserInfo();
    }
  }, []);

  const addGiftee = (email: string) => {
    if (email && !giftees.includes(email)) {
      setGiftees([...giftees, email]);
    }
  };

  const addGifter = (email: string) => {
    if (email && !gifters.includes(email)) {
      setGifters([...gifters, email]);
    }
  };

  const removeGiftee = (email: string) => {
    setGiftees(giftees.filter(e => e !== email));
  };

  const removeGifter = (email: string) => {
    setGifters(gifters.filter(e => e !== email));
  };

  const calculateBudget = () => {
    const total = parseFloat(recommendedAmount) * gifters.length;
    
    // For exchange types, everyone is both gifter and giftee
    const isExchangeType = ['gift-exchange', 'white-elephant', 'gift-circle'].includes(groupType);
    
    if (isExchangeType) {
      // For exchanges, budget per gift is total pool divided by number of participants
      return (total / gifters.length).toFixed(2);
    }
    
    // For gift types, divide by number of giftees
    const perGift = groupType === 'single-giftee' ? total : total / giftees.length;
    return perGift.toFixed(2);
  };

  const getTypeId = (type: string): number => {
    const typeMap: Record<string, number> = {
      'single-giftee': 1,
      'multiple-giftees': 2,
      'individual-gifts': 3,
      'gift-exchange': 4,
      'white-elephant': 5,
      'gift-circle': 6,
    };
    return typeMap[type] || 1;
  };

  const handleCreateGiftGroup = async () => {
    try {
      setIsCreating(true);
      setError(null);

      // Check if user is authenticated
      if (!api.isAuthenticated()) {
        // Save gift group data to localStorage for later
        const giftGroupData = {
          title: eventTitle,
          typeId: getTypeId(groupType),
          giftDate,
          giftTime: groupType === 'white-elephant' ? giftTime : undefined,
          recommendedContribution: parseFloat(recommendedAmount),
          giftees,
          gifters,
          groupType,
        };
        localStorage.setItem('pendingGiftGroup', JSON.stringify(giftGroupData));
        
        toast.info('Please sign in to create your gift group', {
          description: 'Your gift group details have been saved.',
        });
        
        // Navigate to login
        onNavigate('login');
        return;
      }

      // Calculate order by date (7 days before gift date by default)
      const giftDateObj = new Date(giftDate);
      const orderByDateObj = new Date(giftDateObj);
      orderByDateObj.setDate(orderByDateObj.getDate() - 7);
      const orderByDate = orderByDateObj.toISOString().split('T')[0];

      const response = await api.createGiftGroup({
        title: eventTitle,
        typeId: getTypeId(groupType),
        giftDate,
        giftTime: groupType === 'white-elephant' ? giftTime : undefined,
        orderByDate,
        recommendedContribution: parseFloat(recommendedAmount),
        giftees,
        gifters,
      });

      // Clear pending gift group data after successful creation
      localStorage.removeItem('pendingGiftGroup');

      toast.success('Gift group created!', {
        description: `${response.giftGroup.invitationsSent} invitations sent.`,
      });

      // Navigate back to dashboard
      onNavigate('dashboard', { tab: 'todo' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create gift group';
      setError(errorMessage);
      toast.error('Failed to create gift group', {
        description: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-secondary/20 to-accent/20">
      <Card className="w-full max-w-2xl p-8 border border-primary/20 shadow-lg">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    step >= s
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s}
                </div>
                {s < 5 && (
                  <div
                    className={`w-12 h-1 ${
                      step > s ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Step {step} of 5
          </div>
        </div>

        {/* Step 1: Gift Group Type */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-primary mb-2">Select Gift Group Type</h2>
              <p className="text-sm text-muted-foreground">What kind of gifting occasion?</p>
            </div>

            <div className="space-y-2">
              <Label>Gift Group Type</Label>
              <Select value={groupType} onValueChange={setGroupType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single-giftee">Single Giftee - One person receives from many</SelectItem>
                  <SelectItem value="multiple-giftees">Multiple Giftees - Multiple people receive one gift</SelectItem>
                  <SelectItem value="individual-gifts">Individual Gifts - Multiple people receive individual gifts</SelectItem>
                  <SelectItem value="gift-exchange">Gift Exchange - Secret Santa style</SelectItem>
                  <SelectItem value="white-elephant">White Elephant - Pick or steal game</SelectItem>
                  <SelectItem value="gift-circle">Gift Circle - Everyone gifts everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {groupType && (
              <Card className="p-4 bg-primary/5 border-primary/20">
                <p className="text-sm">
                  {groupType === 'single-giftee' && 'One person receives a gift from one or more gifters. Common for birthdays and individual anniversaries. The group is kept secret from the giftee.'}
                  {groupType === 'multiple-giftees' && 'Multiple people receive one shared gift from one or more gifters. Common for wedding anniversaries. The group is kept secret from giftees.'}
                  {groupType === 'individual-gifts' && 'Multiple people receive individual gifts from one or more gifters. Common for team anniversaries and accomplishments. The group is kept secret from giftees.'}
                  {groupType === 'gift-exchange' && 'We pair gifters up to purchase gifts for each other (aka Secret Santa).'}
                  {groupType === 'white-elephant' && 'Everyone secretly purchases wacky gifts individually, then at gift time we create a random order to pick an unopened gift or steal an opened gift (3 steal limit).'}
                  {groupType === 'gift-circle' && 'Everyone collaborates to pick gifts for every member except themselves!'}
                </p>
              </Card>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => onNavigate(isAuthenticated ? 'dashboard' : 'landing')}
                className="border-primary/30"
              >
                {isAuthenticated ? 'Cancel' : 'Back to Home'}
              </Button>
              <Button
                onClick={() => setStep(2)}
                className="bg-primary hover:bg-primary/90"
                disabled={!groupType}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Gift Date */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-primary mb-2">Enter Gift Date</h2>
              <p className="text-sm text-muted-foreground">When should the gift arrive?</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gift-date">Gift Date</Label>
                <div className="relative">
                  <Input
                    id="gift-date"
                    type="date"
                    value={giftDate}
                    onChange={(e) => setGiftDate(e.target.value)}
                    className="md:pl-10"
                  />
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none hidden md:block" />
                </div>
              </div>

              {/* Time entry only for White Elephant */}
              {groupType === 'white-elephant' && (
                <div className="space-y-2">
                  <Label htmlFor="gift-time">Event Time (Required)</Label>
                  <Input
                    id="gift-time"
                    type="time"
                    value={giftTime}
                    onChange={(e) => setGiftTime(e.target.value)}
                  />
                  <Card className="p-3 bg-accent/20 border-accent/40">
                    <p className="text-xs text-muted-foreground">
                      <strong>Important:</strong> An hour before this time, the picking order will be distributed and the first picker will be notified to pick at this time.
                    </p>
                  </Card>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-primary/30"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="bg-primary hover:bg-primary/90"
                disabled={!giftDate || (groupType === 'white-elephant' && !giftTime)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Giftees and Gifters */}
        {step === 3 && (() => {
          // Check if this is an exchange-type group (no giftees needed)
          const isExchangeType = ['gift-exchange', 'white-elephant', 'gift-circle'].includes(groupType);
          
          return (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-primary mb-2">
                  {isExchangeType ? 'Add Participants' : 'Add Giftees & Gifters'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isExchangeType ? 'Who will participate in this exchange?' : 'Who\'s giving and receiving?'}
                </p>
              </div>

              <div className="space-y-4">
                {/* Giftees - Only show for non-exchange types */}
                {!isExchangeType && (
                  <div className="space-y-2">
                    <Label>Giftee(s)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="giftee-input"
                        placeholder="Enter email or phone..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addGiftee((e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        className="bg-primary hover:bg-primary/90"
                        onClick={() => {
                          const input = document.getElementById('giftee-input') as HTMLInputElement;
                          addGiftee(input.value);
                          input.value = '';
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-primary/20 rounded">
                      {giftees.length === 0 && (
                        <span className="text-sm text-muted-foreground">No giftees added</span>
                      )}
                      {giftees.map((email) => (
                        <Badge key={email} variant="secondary" className="gap-1 pr-1">
                          {email}
                          <button
                            type="button"
                            className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeGiftee(email);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gifters */}
                <div className="space-y-2">
                  <Label>{isExchangeType ? 'Participants' : 'Gifter(s)'}</Label>
                  <p className="text-xs text-muted-foreground">
                    You are automatically added {isExchangeType ? 'as a participant' : 'as a gifter'} (you can remove yourself if needed)
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="gifter-input"
                      placeholder="Enter email or phone..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addGifter((e.target as HTMLInputElement).value);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="bg-primary hover:bg-primary/90"
                      onClick={() => {
                        const input = document.getElementById('gifter-input') as HTMLInputElement;
                        addGifter(input.value);
                        input.value = '';
                      }}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-primary/20 rounded">
                    {gifters.length === 0 && (
                      <span className="text-sm text-muted-foreground">No {isExchangeType ? 'participants' : 'gifters'} added</span>
                    )}
                    {gifters.map((email) => (
                      <Badge 
                        key={email} 
                        variant={email === currentUserEmail ? "default" : "secondary"} 
                        className="gap-1 pr-1"
                      >
                        {email}
                        {email === currentUserEmail && <span className="text-xs ml-1">(you)</span>}
                        <button
                          type="button"
                          className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeGifter(email);
                          }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-primary/30"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  className="bg-primary hover:bg-primary/90"
                  disabled={isExchangeType ? gifters.length === 0 : (giftees.length === 0 || gifters.length === 0)}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Step 4: Event Title */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-primary mb-2">Gift Group Title</h2>
              <p className="text-sm text-muted-foreground">AI generated - edit if needed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="event-title">Event Title</Label>
              <Input
                id="event-title"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder="Enter title..."
              />
              <p className="text-xs text-muted-foreground">AI suggests titles based on your inputs</p>
            </div>

            <Card className="p-4 bg-muted/50 border border-primary/20">
              <div className="text-sm space-y-2">
                <div><strong>Summary:</strong></div>
                {(() => {
                  const isExchangeType = ['gift-exchange', 'white-elephant', 'gift-circle'].includes(groupType);
                  if (isExchangeType) {
                    return (
                      <>
                        <div>Participants: {gifters.length}</div>
                        <div className="text-xs text-muted-foreground">(Everyone gives and receives)</div>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <div>Giftees: {giftees.length}</div>
                        <div>Gifters: {gifters.length}</div>
                      </>
                    );
                  }
                })()}
                <div>Type: {groupType}</div>
                <div>Date: {giftDate}</div>
              </div>
            </Card>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setStep(3)}
                className="border-primary/30"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(5)}
                className="bg-primary hover:bg-primary/90"
                disabled={!eventTitle}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Recommended Contribution */}
        {step === 5 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-primary mb-2">Recommended Contribution</h2>
              <p className="text-sm text-muted-foreground">Suggested amount per gifter</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount per Gifter</Label>
              <div className="relative">
                <Input
                  id="amount"
                  type="number"
                  value={recommendedAmount}
                  onChange={(e) => setRecommendedAmount(e.target.value)}
                  className="pl-10"
                  min="0"
                  step="5"
                />
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">
                Set to $0 if everyone can participate in ranking gifts. Everyone can still contribute more but it won't be a mandatory minimum.
              </p>
            </div>

            <Card className="p-4 bg-primary/5 border-primary/20">
              <div className="text-sm space-y-2">
                <div><strong>Budget Calculator:</strong></div>
                <div>{gifters.length} gifters × ${recommendedAmount} = ${(parseFloat(recommendedAmount) * gifters.length).toFixed(2)} total pool</div>
                <div className="pt-2 border-t border-primary/20">
                  <strong>Available per gift: ${calculateBudget()}</strong>
                </div>
              </div>
            </Card>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setStep(4)}
                className="border-primary/30"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => setStep(6)}
                className="bg-primary hover:bg-primary/90"
                disabled={recommendedAmount === '' || isNaN(parseFloat(recommendedAmount)) || parseFloat(recommendedAmount) < 0}
              >
                Review
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Review and Create */}
        {step === 6 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-primary mb-2">Review & Create</h2>
              <p className="text-sm text-muted-foreground">Confirm your gift group details</p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Card className="p-6 border border-primary/20 space-y-4">
              <div>
                <div className="text-xs text-muted-foreground uppercase">Title</div>
                <div>{eventTitle}</div>
              </div>
              
              <div className="border-t border-primary/10 pt-2">
                <div className="text-xs text-muted-foreground uppercase mb-1">Gift Date</div>
                <div>{giftDate}{groupType === 'white-elephant' && giftTime && ` at ${giftTime}`}</div>
              </div>
              
              <div className="border-t border-primary/10 pt-2">
                <div className="text-xs text-muted-foreground uppercase mb-1">Type</div>
                <div>{groupType}</div>
              </div>
              
              <div className="border-t border-primary/10 pt-2">
                <div className="text-xs text-muted-foreground uppercase mb-1">Giftees ({giftees.length})</div>
                <div className="flex flex-wrap gap-1">
                  {giftees.map((email) => (
                    <Badge key={email} variant="secondary">{email}</Badge>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-primary/10 pt-2">
                <div className="text-xs text-muted-foreground uppercase mb-1">Gifters ({gifters.length})</div>
                <div className="flex flex-wrap gap-1">
                  {gifters.map((email) => (
                    <Badge key={email} variant="secondary">{email}</Badge>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-primary/10 pt-2">
                <div className="text-xs text-muted-foreground uppercase mb-1">Recommended Contribution</div>
                <div className="text-primary">${recommendedAmount} per gifter</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Total Pool: ${(parseFloat(recommendedAmount) * gifters.length).toFixed(2)}
                </div>
              </div>
            </Card>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setStep(5)}
                className="border-primary/30"
                disabled={isCreating}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCreateGiftGroup}
                className="bg-primary hover:bg-primary/90"
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {isAuthenticated ? 'Create & Send Invites' : 'Authenticate Yourself'}
                  </>
                )}
              </Button>
            </div>

            <div className="border border-dashed border-primary/30 p-4 rounded-lg bg-accent/10">
              <p className="text-xs text-muted-foreground">
                {isAuthenticated ? (
                  <>
                    <strong>Note:</strong> After clicking "Create & Send Invites", 
                    the gift group is created and all gifters receive invitations via their 
                    preferred contact method (email/SMS).
                  </>
                ) : (
                  <>
                    <strong>Note:</strong> You need to authenticate yourself before creating 
                    a gift group. Your gift group details will be saved and restored after you sign in.
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}